import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Save, Eye, EyeOff, Plus, Trash2, Wand2, Loader2, Image, Video,
  Type, Layout, Users, Mail, Palette, Settings, Upload, Link2,
  RotateCcw, MessageSquareQuote, Minus, GripVertical, Grid3X3, BookOpen,
  ShoppingBag, X as XIcon, Sparkles, CalendarDays
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import AIAgentWidget from './AIAgentWidget';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl transition-all duration-300 ${className}`} {...props}>{children}</div>
);

const blockIcons = {
  hero: Video, banner: Image, products: Layout, about: Users,
  gallery: Image, signup: Mail, custom_text: Type, custom_image: Image,
  video: Video, testimonial: MessageSquareQuote, spacer: Minus,
  booking: CalendarDays
};
const blockLabels = {
  hero: 'Hero Section', banner: 'Web Banner', products: 'Featured Products',
  about: 'About Section', gallery: 'Gallery', signup: 'Email Signup',
  custom_text: 'Custom Text', custom_image: 'Custom Image',
  video: 'Video Embed', testimonial: 'Testimonial', spacer: 'Spacer',
  booking: 'Booking Calendar'
};
const BLOCK_TYPES = [
  { type: 'hero', label: 'Hero Section', desc: 'Full-screen hero with video/image' },
  { type: 'banner', label: 'Web Banner', desc: 'Image banner with overlay text' },
  { type: 'products', label: 'Products Grid', desc: 'Featured products display' },
  { type: 'about', label: 'About Section', desc: 'Brand story and image' },
  { type: 'gallery', label: 'Gallery', desc: 'Image gallery grid' },
  { type: 'donations', label: 'Donations', desc: 'Accept donations via card or PayPal' },
  { type: 'signup', label: 'Email Signup', desc: 'Newsletter subscription form' },
  { type: 'custom_text', label: 'Custom Text', desc: 'Free-form text block' },
  { type: 'custom_image', label: 'Custom Image', desc: 'Image with caption' },
  { type: 'video', label: 'Video Embed', desc: 'Embed a video' },
  { type: 'testimonial', label: 'Testimonial', desc: 'Customer quote' },
  { type: 'spacer', label: 'Spacer', desc: 'Visual spacing element' },
  { type: 'booking', label: 'Booking Calendar', desc: 'Embed booking page on site' },
];

const MasterImageUploader = ({ value, onChange, label, hint, siteId, blockId, linkValue, onLinkChange }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const token = localStorage.getItem('masterToken');
  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/master/upload/${siteId}?category=content`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      onChange(process.env.REACT_APP_BACKEND_URL + res.data.url);
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };
  const handleDrop = (e) => { e.preventDefault(); const f = e.dataTransfer?.files?.[0]; if (f?.type?.startsWith('image/')) handleUpload(f); };
  const imgSrc = value?.startsWith('/api') ? process.env.REACT_APP_BACKEND_URL + value : value;
  return (
    <div className="space-y-1.5">
      {label && <Label className="text-[#9CA3AF] text-xs">{label}</Label>}
      {hint && <p className="text-[10px] text-[#64748B]">{hint}</p>}
      <div className="relative border-2 border-dashed border-white/10 rounded-xl overflow-hidden hover:border-[#8B5CF6]/40 transition-colors cursor-pointer group" onClick={() => fileRef.current?.click()} onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} data-testid={`upload-${blockId}`}>
        {value ? (
          <div className="relative"><img src={imgSrc} alt="" className="w-full h-40 object-cover" /><div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><Upload size={18} className="text-white" /></div></div>
        ) : (
          <div className="h-32 flex flex-col items-center justify-center text-[#64748B]">
            {uploading ? <Loader2 size={24} className="animate-spin text-[#8B5CF6]" /> : <><Upload size={24} className="mb-2" /><span className="text-xs">Click or drag to upload</span></>}
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} />
      </div>
      {value && <button onClick={(e) => { e.stopPropagation(); onChange(''); }} className="text-[10px] text-[#FF6B6B] hover:underline">Remove image</button>}
      {value && onLinkChange && (
        <div className="relative"><Link2 size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#64748B]" /><Input value={linkValue || ''} onChange={(e) => onLinkChange(e.target.value)} placeholder="Add link to image (optional)" className="bg-white/5 border-white/10 text-white text-xs h-8 pl-8 rounded-lg" /></div>
      )}
    </div>
  );
};

const MasterVideoUploader = ({ value, onChange, siteId, blockId }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const token = localStorage.getItem('masterToken');
  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/master/upload/${siteId}?category=videos`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      onChange(process.env.REACT_APP_BACKEND_URL + res.data.url);
      toast.success('Video uploaded');
    } catch { toast.error('Video upload failed'); }
    finally { setUploading(false); }
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-[#9CA3AF] text-xs">Video</Label>
      <div className="flex gap-2">
        <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Paste URL or upload" className="bg-white/5 border-white/10 text-white text-xs flex-1 rounded-lg" />
        <button type="button" onClick={() => fileRef.current?.click()} className="px-3 py-1.5 bg-[#8B5CF6]/20 text-[#A78BFA] border border-[#8B5CF6]/20 rounded-lg text-xs hover:bg-[#8B5CF6]/30 transition-colors flex items-center gap-1" data-testid={`upload-video-${blockId}`}>
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload
        </button>
      </div>
      <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} />
    </div>
  );
};

const MasterGalleryEditor = ({ images = [], onChange, layout = 'grid', onLayoutChange, siteId, blockId }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const token = localStorage.getItem('masterToken');
  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const newImgs = [...images];
    for (const file of Array.from(files)) {
      if (newImgs.length >= 50) break;
      if (!file.type.startsWith('image/')) continue;
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await axios.post(`${API}/master/upload/${siteId}?category=gallery`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        newImgs.push({ url: process.env.REACT_APP_BACKEND_URL + res.data.url, caption: '' });
      } catch { /* skip */ }
    }
    onChange(newImgs);
    setUploading(false);
  };
  return (
    <div className="space-y-3" data-testid={`gallery-editor-${blockId}`}>
      <div className="flex items-center justify-between">
        <Label className="text-[#9CA3AF] text-xs">Photos ({images.length}/50)</Label>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => onLayoutChange('grid')} className={`p-1.5 rounded-lg text-xs ${layout === 'grid' ? 'bg-[#8B5CF6]/20 text-[#A78BFA]' : 'text-[#64748B] hover:text-white'}`}><Grid3X3 size={14} /></button>
          <button type="button" onClick={() => onLayoutChange('lookbook')} className={`p-1.5 rounded-lg text-xs ${layout === 'lookbook' ? 'bg-[#8B5CF6]/20 text-[#A78BFA]' : 'text-[#64748B] hover:text-white'}`}><BookOpen size={14} /></button>
        </div>
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {images.map((img, idx) => (
          <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border border-white/10">
            <img src={typeof img === 'string' ? img : img.url} alt="" className="w-full h-full object-cover" />
            <button onClick={() => onChange(images.filter((_, i) => i !== idx))} className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/70 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={10} className="text-white" /></button>
          </div>
        ))}
        {images.length < 50 && (
          <div className="aspect-square border-2 border-dashed border-white/10 rounded-lg flex items-center justify-center cursor-pointer hover:border-[#8B5CF6]/40 transition-colors" onClick={() => fileRef.current?.click()} onDrop={(e) => { e.preventDefault(); handleUpload(e.dataTransfer?.files); }} onDragOver={(e) => e.preventDefault()}>
            {uploading ? <Loader2 size={14} className="animate-spin text-[#8B5CF6]" /> : <Plus size={14} className="text-[#64748B]" />}
          </div>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleUpload(e.target.files)} />
    </div>
  );
};

const ButtonsEditor = ({ buttons = [], onChange, blockId }) => (
  <div className="space-y-2 pt-3 border-t border-white/5">
    <div className="flex items-center justify-between">
      <Label className="text-[#9CA3AF] text-xs">Buttons</Label>
      <button onClick={() => onChange([...buttons, { text: 'New Button', link: '#' }])} className="text-xs text-[#A78BFA] hover:text-white flex items-center gap-1" data-testid={`add-btn-${blockId}`}><Plus size={12} /> Add</button>
    </div>
    {buttons.map((btn, i) => (
      <div key={i} className="flex gap-2 items-center">
        <Input value={btn.text || ''} onChange={(e) => { const b = [...buttons]; b[i] = { ...b[i], text: e.target.value }; onChange(b); }} placeholder="Label" className="bg-white/5 border-white/10 text-white text-xs h-8 rounded-lg" />
        <div className="relative flex-1"><Link2 size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#64748B]" /><Input value={btn.link || ''} onChange={(e) => { const b = [...buttons]; b[i] = { ...b[i], link: e.target.value }; onChange(b); }} placeholder="URL" className="bg-white/5 border-white/10 text-white text-xs h-8 pl-7 rounded-lg" /></div>
        <button onClick={() => onChange(buttons.filter((_, idx) => idx !== i))} className="text-[#64748B] hover:text-[#FF6B6B] shrink-0"><Trash2 size={12} /></button>
      </div>
    ))}
  </div>
);

// Sortable featured product item for Master
function SortableFeaturedProduct({ product, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: product.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const imgSrc = product.images?.[0] || product.image_url || '';
  return (
    <div ref={setNodeRef} style={style} className={`flex items-center gap-3 p-2.5 rounded-xl bg-white/5 border border-white/10 ${isDragging ? 'ring-1 ring-[#8B5CF6]/50' : ''}`}>
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-[#64748B] hover:text-white shrink-0"><GripVertical size={14} /></div>
      {imgSrc ? <img src={imgSrc} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" /> : <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><ShoppingBag size={14} className="text-[#64748B]" /></div>}
      <div className="flex-1 min-w-0"><p className="text-xs font-medium text-white truncate">{product.name}</p><p className="text-[10px] text-[#00CC99]">${product.price?.toFixed(2)}</p></div>
      <button onClick={onRemove} className="text-[#64748B] hover:text-[#FF6B6B] shrink-0 p-1"><XIcon size={12} /></button>
    </div>
  );
}

// Featured Products Editor for Master
const MasterFeaturedProductsEditor = ({ block, updateBlock, allProducts }) => {
  const featuredIds = block.content.featured_product_ids || [];
  const featuredProducts = featuredIds.map(id => allProducts.find(p => p.id === id)).filter(Boolean);
  const availableProducts = allProducts.filter(p => !featuredIds.includes(p.id) && p.is_active !== false);
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState('');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = featuredIds.indexOf(active.id);
    const newIdx = featuredIds.indexOf(over.id);
    updateBlock(block.id, 'featured_product_ids', arrayMove(featuredIds, oldIdx, newIdx));
  };

  const filteredAvailable = availableProducts.filter(p => p.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-3">
      <div><Label className="text-[#9CA3AF] text-xs">Section Title</Label><Input value={block.content.title || ''} onChange={(e) => updateBlock(block.id, 'title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div><Label className="text-[#9CA3AF] text-xs">Subtitle</Label><Input value={block.content.subtitle || ''} onChange={(e) => updateBlock(block.id, 'subtitle', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div className="pt-3 border-t border-white/5">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-[#9CA3AF] text-xs">Homepage Products ({featuredProducts.length})</Label>
          <button onClick={() => setShowPicker(true)} className="text-xs text-[#A78BFA] hover:text-white flex items-center gap-1"><Plus size={12} /> Add Product</button>
        </div>
        {featuredProducts.length > 0 ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={featuredIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5">
                {featuredProducts.map((p) => (
                  <SortableFeaturedProduct key={p.id} product={p} onRemove={() => updateBlock(block.id, 'featured_product_ids', featuredIds.filter(id => id !== p.id))} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="text-center py-4 text-[#64748B] text-xs border border-dashed border-white/10 rounded-xl">No products selected.</div>
        )}
      </div>
      <Dialog open={showPicker} onOpenChange={setShowPicker}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-md max-h-[70vh]">
          <DialogHeader><DialogTitle className="text-lg">Add Products to Homepage</DialogTitle></DialogHeader>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products..." className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl mb-3" />
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {filteredAvailable.length > 0 ? filteredAvailable.map((p) => {
              const imgSrc = p.images?.[0] || p.image_url || '';
              return (
                <button key={p.id} onClick={() => { updateBlock(block.id, 'featured_product_ids', [...featuredIds, p.id]); setShowPicker(false); setSearch(''); }} className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 hover:border-[#8B5CF6]/40 hover:bg-[#8B5CF6]/10 transition-all text-left">
                  {imgSrc ? <img src={imgSrc} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" /> : <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><ShoppingBag size={14} className="text-[#64748B]" /></div>}
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium text-white truncate">{p.name}</p><p className="text-xs text-[#00CC99]">${p.price?.toFixed(2)}</p></div>
                  <Plus size={14} className="text-[#A78BFA] shrink-0" />
                </button>
              );
            }) : <p className="text-center text-[#64748B] text-xs py-6">No products available.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Mini live preview
const LivePreview = ({ blocks, allProducts }) => {
  const visibleBlocks = blocks.filter(b => b.is_visible !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
  return (
    <div className="bg-white text-black min-h-full" style={{ fontSize: '6px' }} data-testid="master-live-preview-renderer">
      {visibleBlocks.map((block) => {
        const c = block.content || {};
        switch (block.type) {
          case 'hero':
            return (
              <div key={block.id} id={`preview-${block.id}`} className="relative" style={{ height: 120, background: c.image_url ? `url(${c.image_url}) center/cover` : '#1a1a1a' }}>
                <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white text-center p-2">
                  <p style={{ fontSize: 10, fontWeight: 700 }}>{c.title}</p>
                  <p style={{ fontSize: 6, opacity: 0.8 }}>{c.tagline}</p>
                  {c.buttons?.map((btn, i) => <span key={i} className="inline-block mt-1 px-2 py-0.5 bg-white text-black" style={{ fontSize: 5 }}>{btn.text}</span>)}
                </div>
              </div>
            );
          case 'banner':
            return (
              <div key={block.id} id={`preview-${block.id}`} className="relative" style={{ height: 80, background: c.image_url ? `url(${c.image_url}) center/cover` : '#333' }}>
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center"><p style={{ fontSize: 8, color: 'white', fontWeight: 600 }}>{c.text}</p></div>
              </div>
            );
          case 'products': {
            const featuredIds = c.featured_product_ids || [];
            const featured = featuredIds.map(id => allProducts.find(p => p.id === id)).filter(Boolean);
            return (
              <div key={block.id} id={`preview-${block.id}`} className="p-3 text-center">
                <p style={{ fontSize: 8, fontWeight: 700 }}>{c.title}</p>
                <p style={{ fontSize: 5, color: '#999' }}>{c.subtitle}</p>
                <div className="flex gap-1 justify-center mt-1 flex-wrap">
                  {featured.length > 0 ? featured.slice(0, 6).map(p => {
                    const img = p.images?.[0] || p.image_url || '';
                    return (
                      <div key={p.id} className="w-12 text-center">
                        {img ? <img src={img} alt="" className="w-12 h-14 object-cover rounded" /> : <div className="w-12 h-14 bg-gray-200 rounded" />}
                        <p style={{ fontSize: 4, marginTop: 2 }} className="truncate">{p.name}</p>
                      </div>
                    );
                  }) : [1,2,3].map(i => <div key={i} className="w-8 h-10 bg-gray-200 rounded" />)}
                </div>
              </div>
            );
          }
          case 'about':
            return (
              <div key={block.id} id={`preview-${block.id}`} className="p-3 flex gap-2">
                {c.image_url && <div className="w-12 h-16 shrink-0 rounded overflow-hidden"><img src={c.image_url} alt="" className="w-full h-full object-cover" /></div>}
                <div><p style={{ fontSize: 7, fontWeight: 700 }}>{c.title}</p><p style={{ fontSize: 5, color: '#666' }}>{c.text?.substring(0, 80)}</p></div>
              </div>
            );
          case 'gallery':
            return (
              <div key={block.id} id={`preview-${block.id}`} className="p-2">
                <p style={{ fontSize: 7, fontWeight: 700, textAlign: 'center' }}>{c.title}</p>
                <div className={`mt-1 ${c.layout === 'lookbook' ? 'space-y-1' : 'grid grid-cols-3 gap-0.5'}`}>
                  {(c.images || []).slice(0, 6).map((img, i) => (
                    <div key={i} className={`bg-gray-200 rounded overflow-hidden ${c.layout === 'lookbook' ? 'h-12' : 'aspect-square'}`}>
                      <img src={typeof img === 'string' ? img : img.url} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            );
          case 'signup':
            return (
              <div key={block.id} id={`preview-${block.id}`} className="p-3 text-center" style={{ background: '#f5f5f5' }}>
                <p style={{ fontSize: 7, fontWeight: 700 }}>{c.title}</p>
                <p style={{ fontSize: 5, color: '#666' }}>{c.subtitle}</p>
                <div className="flex gap-1 justify-center mt-1"><div className="h-3 w-16 bg-white border border-gray-300 rounded" /><div className="h-3 w-6 bg-black text-white flex items-center justify-center rounded" style={{ fontSize: 4 }}>Join</div></div>
              </div>
            );
          case 'donations':
            return (
              <div key={block.id} id={`preview-${block.id}`} className="p-3 text-center" style={{ background: '#1E1E3A' }}>
                <div className="w-4 h-4 mx-auto rounded-full bg-pink-500 mb-1 flex items-center justify-center"><span style={{ fontSize: 6, color: 'white' }}>♥</span></div>
                <p style={{ fontSize: 7, fontWeight: 700, color: 'white' }}>{c.title || 'Donations'}</p>
                <p style={{ fontSize: 5, color: '#9CA3AF' }}>{c.subtitle}</p>
                <div className="flex gap-1 justify-center mt-1">
                  {(c.amounts || [100, 500, 1000]).map(a => (<div key={a} className="px-1 py-0.5 rounded bg-white/10 border border-white/20" style={{ fontSize: 4, color: 'white' }}>${a}</div>))}
                </div>
              </div>
            );
          case 'custom_text':
            return (
              <div key={block.id} id={`preview-${block.id}`} className="p-3" style={{ textAlign: c.alignment || 'center' }}>
                {c.title && <p style={{ fontSize: 7, fontWeight: 700 }}>{c.title}</p>}
                <p style={{ fontSize: 5, color: '#666' }}>{c.text}</p>
              </div>
            );
          case 'custom_image':
            return (
              <div key={block.id} id={`preview-${block.id}`} className="p-2 text-center">
                {c.image_url && <img src={c.image_url} alt="" className="mx-auto rounded" style={{ maxHeight: 60 }} />}
                {c.caption && <p style={{ fontSize: 5, color: '#999', marginTop: 2 }}>{c.caption}</p>}
              </div>
            );
          case 'video':
            return (
              <div key={block.id} id={`preview-${block.id}`} className="p-2 text-center">
                <div className="bg-gray-900 rounded flex items-center justify-center" style={{ height: 50 }}><Video size={12} className="text-gray-500" /></div>
                {c.title && <p style={{ fontSize: 5, color: '#666', marginTop: 2 }}>{c.title}</p>}
              </div>
            );
          case 'testimonial':
            return (
              <div key={block.id} id={`preview-${block.id}`} className="p-3 text-center" style={{ background: '#fafafa' }}>
                <p style={{ fontSize: 6, fontStyle: 'italic', color: '#333' }}>"{c.quote}"</p>
                <p style={{ fontSize: 5, fontWeight: 600, marginTop: 2 }}>{c.author}</p>
                {c.role && <p style={{ fontSize: 4, color: '#999' }}>{c.role}</p>}
              </div>
            );
          case 'spacer':
            return <div key={block.id} id={`preview-${block.id}`} style={{ height: parseInt(c.height) / 4 || 20 }} />;
          default:
            return null;
        }
      })}
      {visibleBlocks.length === 0 && <div className="p-8 text-center text-gray-400" style={{ fontSize: 8 }}>Add blocks to see preview</div>}
    </div>
  );
};

// Sortable block wrapper
function SortableBlock({ block, isActive, onToggle, onDelete, onToggleVis, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 10 : 0 };
  const Icon = blockIcons[block.type] || Layout;
  return (
    <div ref={setNodeRef} style={style}>
      <GlassCard className={`${isActive ? 'border-[#8B5CF6]/40 shadow-[0_0_20px_rgba(139,92,246,0.15)]' : ''} ${isDragging ? 'ring-2 ring-[#8B5CF6]/50' : ''}`} data-testid={`editor-block-${block.id}`}>
        <div className="flex items-center justify-between px-4 py-3 cursor-pointer" onClick={onToggle}>
          <div className="flex items-center gap-3">
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-[#64748B] hover:text-white" data-testid={`drag-handle-${block.id}`}>
              <GripVertical size={14} />
            </div>
            <div className={`p-1.5 rounded-lg ${isActive ? 'bg-[#8B5CF6]/20' : 'bg-white/5'}`}>
              <Icon size={14} className={isActive ? 'text-[#A78BFA]' : 'text-[#9CA3AF]'} />
            </div>
            <span className="text-sm font-medium text-white">{blockLabels[block.type] || block.type}</span>
            {!block.is_visible && <span className="text-[10px] text-[#64748B] ml-1">(hidden)</span>}
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={(e) => { e.stopPropagation(); onToggleVis(); }} className={`p-1.5 rounded-lg ${block.is_visible ? 'text-[#00CC99]' : 'text-[#64748B]'}`} data-testid={`toggle-vis-${block.id}`}>{block.is_visible ? <Eye size={13} /> : <EyeOff size={13} />}</button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1.5 text-[#64748B] hover:text-[#FF6B6B] rounded-lg" data-testid={`delete-block-${block.id}`}><Trash2 size={13} /></button>
          </div>
        </div>
        {isActive && <div className="px-4 pb-4 border-t border-white/5 pt-4" data-testid={`editor-content-${block.id}`}>{children}</div>}
      </GlassCard>
    </div>
  );
}

export default function MasterSiteEditor() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeBlock, setActiveBlock] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});
  const [showPreview, setShowPreview] = useState(true);
  const previewRef = useRef(null);

  const token = localStorage.getItem('masterToken');
  const headers = { Authorization: `Bearer ${token}` };
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const fetchData = useCallback(async () => {
    try {
      const [siteRes, contentRes, settingsRes] = await Promise.all([
        axios.get(`${API}/master/sites/${siteId}`, { headers }),
        axios.get(`${API}/master/sites/${siteId}/content`, { headers }),
        axios.get(`${API}/master/sites/${siteId}/settings`, { headers }),
      ]);
      setSite(siteRes.data);
      setBlocks(contentRes.data.blocks || []);
      setSiteSettings(settingsRes.data);
      setSettingsForm({
        brand_name: settingsRes.data.brand_name || '',
        tagline: settingsRes.data.tagline || '',
        accent_color: settingsRes.data.accent_color || '#8B5CF6',
      });
      // Fetch products for this site
      try {
        const productsRes = await axios.get(`${API}/master/sites/${siteId}/products`, { headers });
        setAllProducts(productsRes.data || []);
      } catch {
        setAllProducts([]);
      }
    } catch {
      toast.error('Failed to load site data');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Unsaved changes warning
  useEffect(() => {
    const handler = (e) => { if (hasChanges) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasChanges]);

  const scrollPreviewToBlock = useCallback((blockId) => {
    if (!previewRef.current) return;
    const previewEl = previewRef.current.querySelector(`#preview-${window.CSS.escape(blockId)}`);
    if (previewEl) {
      previewEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      previewEl.style.outline = '2px solid #8B5CF6';
      previewEl.style.outlineOffset = '-2px';
      setTimeout(() => { previewEl.style.outline = 'none'; }, 1500);
    }
  }, []);

  const handleToggleBlock = useCallback((blockId) => {
    const isOpening = activeBlock !== blockId;
    setActiveBlock(isOpening ? blockId : null);
    if (isOpening && showPreview) {
      setTimeout(() => scrollPreviewToBlock(blockId), 100);
    }
  }, [activeBlock, showPreview, scrollPreviewToBlock]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/master/sites/${siteId}/content`, { blocks }, { headers });
      toast.success('Content saved');
      setHasChanges(false);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/master/sites/${siteId}/settings`, settingsForm, { headers });
      toast.success('Settings saved');
      setShowSettings(false);
      fetchData();
    } catch { toast.error('Failed to save settings'); }
  };

  const updateBlock = useCallback((blockId, field, value) => {
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, content: { ...b.content, [field]: value } } : b));
    setHasChanges(true);
  }, []);

  const toggleVisibility = (blockId) => { setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, is_visible: !b.is_visible } : b)); setHasChanges(true); };

  const deleteBlock = (blockId) => {
    if (!window.confirm('Delete this block?')) return;
    setBlocks(prev => { const f = prev.filter(b => b.id !== blockId); f.forEach((b, i) => b.order = i); return f; });
    if (activeBlock === blockId) setActiveBlock(null);
    setHasChanges(true);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setBlocks(prev => {
      const oldIndex = prev.findIndex(b => b.id === active.id);
      const newIndex = prev.findIndex(b => b.id === over.id);
      const reordered = arrayMove(prev, oldIndex, newIndex);
      reordered.forEach((b, i) => b.order = i);
      return reordered;
    });
    setHasChanges(true);
  };

  const addBlock = async (type) => {
    try {
      const res = await axios.post(`${API}/master/sites/${siteId}/blocks`, { type }, { headers });
      setBlocks(res.data.blocks);
      setShowAddBlock(false);
      setActiveBlock(res.data.block.id);
      setHasChanges(true);
      toast.success(`${blockLabels[type] || type} added`);
    } catch { toast.error('Failed to add block'); }
  };

  const handleAIRedesign = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const targetIds = activeBlock ? [activeBlock] : null;
      const res = await axios.post(`${API}/master/sites/${siteId}/ai-redesign`, {
        prompt: aiPrompt, target_blocks: targetIds
      }, { headers });
      setAiSuggestions(res.data);
      toast.success('AI suggestions ready');
    } catch { toast.error('AI redesign failed'); }
    finally { setAiLoading(false); }
  };

  const applyAISuggestions = () => {
    if (!aiSuggestions?.suggested_blocks) return;
    const suggestedMap = {};
    aiSuggestions.suggested_blocks.forEach(b => { suggestedMap[b.id] = b; });
    setBlocks(prev => prev.map(block => suggestedMap[block.id] ? { ...block, content: { ...block.content, ...suggestedMap[block.id].content } } : block));
    setHasChanges(true);
    setAiSuggestions(null);
    setShowAIPanel(false);
    setAiPrompt('');
    toast.success('AI suggestions applied');
  };

  // AI Image Generation
  const [showAIImage, setShowAIImage] = useState(false);
  const [aiImagePrompt, setAiImagePrompt] = useState('');
  const [aiImageLoading, setAiImageLoading] = useState(false);
  const [aiGeneratedImage, setAiGeneratedImage] = useState(null);

  const handleGenerateImage = async () => {
    if (!aiImagePrompt.trim()) return;
    setAiImageLoading(true);
    setAiGeneratedImage(null);
    try {
      const res = await axios.post(`${API}/master/ai/generate-image`, { prompt: aiImagePrompt, site_id: siteId }, { headers });
      setAiGeneratedImage(res.data.url);
      toast.success('Image generated!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Image generation failed'); }
    finally { setAiImageLoading(false); }
  };

  const applyAIImage = (blockId, field) => {
    if (!aiGeneratedImage) return;
    updateBlock(blockId, field, aiGeneratedImage);
    setShowAIImage(false);
    setAiGeneratedImage(null);
    setAiImagePrompt('');
    toast.success('AI image applied to block');
  };

  const renderBlockEditor = (block) => {
    const u = (field, value) => updateBlock(block.id, field, value);
    switch (block.type) {
      case 'hero':
        return (<div className="space-y-3">
          <MasterImageUploader value={block.content.image_url} onChange={(v) => u('image_url', v)} label="Hero Image" hint="1920x1080" siteId={siteId} blockId={block.id + '-hero'} linkValue={block.content.image_link} onLinkChange={(v) => u('image_link', v)} />
          <MasterVideoUploader value={block.content.video_url} onChange={(v) => u('video_url', v)} siteId={siteId} blockId={block.id} />
          <div><Label className="text-[#9CA3AF] text-xs">Title</Label><Input value={block.content.title || ''} onChange={(e) => u('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" data-testid="editor-hero-title" /></div>
          <div><Label className="text-[#9CA3AF] text-xs">Tagline</Label><Input value={block.content.tagline || ''} onChange={(e) => u('tagline', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" data-testid="editor-hero-tagline" /></div>
          <ButtonsEditor buttons={block.content.buttons || []} onChange={(v) => u('buttons', v)} blockId={block.id} />
        </div>);
      case 'banner':
        return (<div className="space-y-3">
          <MasterImageUploader value={block.content.image_url} onChange={(v) => u('image_url', v)} label="Banner Image" siteId={siteId} blockId={block.id + '-banner'} linkValue={block.content.image_link} onLinkChange={(v) => u('image_link', v)} />
          <div><Label className="text-[#9CA3AF] text-xs">Overlay Text</Label><Input value={block.content.text || ''} onChange={(e) => u('text', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
          <ButtonsEditor buttons={block.content.buttons || []} onChange={(v) => u('buttons', v)} blockId={block.id} />
        </div>);
      case 'about':
        return (<div className="space-y-3">
          <MasterImageUploader value={block.content.image_url} onChange={(v) => u('image_url', v)} label="Portrait" siteId={siteId} blockId={block.id + '-about'} linkValue={block.content.image_link} onLinkChange={(v) => u('image_link', v)} />
          <div><Label className="text-[#9CA3AF] text-xs">Title</Label><Input value={block.content.title || ''} onChange={(e) => u('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
          <div><Label className="text-[#9CA3AF] text-xs">Bio</Label><Textarea value={block.content.text || ''} onChange={(e) => u('text', e.target.value)} rows={3} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
          <div><Label className="text-[#9CA3AF] text-xs">Vision</Label><Input value={block.content.vision || ''} onChange={(e) => u('vision', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
        </div>);
      case 'custom_image':
        return (<div className="space-y-3">
          <MasterImageUploader value={block.content.image_url} onChange={(v) => u('image_url', v)} label="Image" siteId={siteId} blockId={block.id + '-img'} linkValue={block.content.image_link} onLinkChange={(v) => u('image_link', v)} />
          <div><Label className="text-[#9CA3AF] text-xs">Caption</Label><Input value={block.content.caption || ''} onChange={(e) => u('caption', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
        </div>);
      case 'gallery':
        return (<div className="space-y-3">
          <div><Label className="text-[#9CA3AF] text-xs">Section Title</Label><Input value={block.content.title || ''} onChange={(e) => u('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
          <MasterGalleryEditor images={block.content.images || []} onChange={(v) => u('images', v)} layout={block.content.layout || 'grid'} onLayoutChange={(v) => u('layout', v)} siteId={siteId} blockId={block.id} />
        </div>);
      case 'products':
        return <MasterFeaturedProductsEditor block={block} updateBlock={updateBlock} allProducts={allProducts} />;
      case 'donations':
        return (<div className="space-y-3">
          <div><Label className="text-[#9CA3AF] text-xs">Title</Label><Input value={block.content.title || ''} onChange={(e) => u('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
          <div><Label className="text-[#9CA3AF] text-xs">Subtitle / Message</Label><Textarea value={block.content.subtitle || ''} onChange={(e) => u('subtitle', e.target.value)} rows={2} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
          <div><Label className="text-[#9CA3AF] text-xs">Preset Amounts (comma-separated)</Label><Input value={(block.content.amounts || []).join(', ')} onChange={(e) => u('amounts', e.target.value.split(',').map(v => parseInt(v.trim())).filter(v => v > 0))} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" placeholder="100, 500, 1000" /></div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-[#9CA3AF]"><input type="checkbox" checked={block.content.accept_card !== false} onChange={(e) => u('accept_card', e.target.checked)} className="rounded" /> Credit Card</label>
            <label className="flex items-center gap-2 text-sm text-[#9CA3AF]"><input type="checkbox" checked={block.content.accept_paypal !== false} onChange={(e) => u('accept_paypal', e.target.checked)} className="rounded" /> PayPal</label>
          </div>
          {block.content.accept_paypal && (<div><Label className="text-[#9CA3AF] text-xs">PayPal Email</Label><Input value={block.content.paypal_email || ''} onChange={(e) => u('paypal_email', e.target.value)} placeholder="your@paypal.com" className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>)}
        </div>);
      case 'signup':
        return (<div className="space-y-3">
          <div><Label className="text-[#9CA3AF] text-xs">Title</Label><Input value={block.content.title || ''} onChange={(e) => u('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
          <div><Label className="text-[#9CA3AF] text-xs">Subtitle</Label><Input value={block.content.subtitle || ''} onChange={(e) => u('subtitle', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
        </div>);
      case 'custom_text':
        return (<div className="space-y-3">
          <div><Label className="text-[#9CA3AF] text-xs">Title</Label><Input value={block.content.title || ''} onChange={(e) => u('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
          <div><Label className="text-[#9CA3AF] text-xs">Text</Label><Textarea value={block.content.text || ''} onChange={(e) => u('text', e.target.value)} rows={3} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
          <div><Label className="text-[#9CA3AF] text-xs">Alignment</Label>
            <select value={block.content.alignment || 'center'} onChange={(e) => u('alignment', e.target.value)} className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm mt-1"><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select>
          </div>
        </div>);
      case 'video':
        return (<div className="space-y-3">
          <MasterVideoUploader value={block.content.video_url} onChange={(v) => u('video_url', v)} siteId={siteId} blockId={block.id} />
          <div><Label className="text-[#9CA3AF] text-xs">Title</Label><Input value={block.content.title || ''} onChange={(e) => u('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
        </div>);
      case 'testimonial':
        return (<div className="space-y-3">
          <div><Label className="text-[#9CA3AF] text-xs">Quote</Label><Textarea value={block.content.quote || ''} onChange={(e) => u('quote', e.target.value)} rows={2} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
          <div><Label className="text-[#9CA3AF] text-xs">Author</Label><Input value={block.content.author || ''} onChange={(e) => u('author', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
          <div><Label className="text-[#9CA3AF] text-xs">Role</Label><Input value={block.content.role || ''} onChange={(e) => u('role', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
        </div>);
      case 'spacer':
        return <div><Label className="text-[#9CA3AF] text-xs">Height</Label><Input value={block.content.height || '80px'} onChange={(e) => u('height', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>;
      default:
        return <p className="text-[#64748B] text-sm">No editor for this block type.</p>;
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={32} className="animate-spin text-[#8B5CF6]" /></div>;

  const sortedBlocks = [...blocks].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="space-y-5" data-testid="master-site-editor">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/master/sites')} className="text-[#9CA3AF] hover:text-white transition-colors" data-testid="back-to-sites"><ArrowLeft size={20} /></button>
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="editor-site-name">{site?.brand_name || site?.name}</h1>
            <p className="text-xs text-[#64748B]">{site?.domain}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowPreview(p => !p)} className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${showPreview ? 'bg-[#8B5CF6]/20 border-[#8B5CF6]/40 text-[#A78BFA]' : 'bg-white/5 border-white/10 text-[#9CA3AF]'}`} data-testid="toggle-preview-btn">
            <Eye size={14} /> {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
          <button onClick={() => setShowSettings(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-white/5 border border-white/10 text-[#9CA3AF] hover:text-white transition-all" data-testid="site-settings-btn"><Settings size={14} /> Settings</button>
          <button onClick={() => setShowAIPanel(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] transition-all" data-testid="ai-redesign-btn"><Wand2 size={14} /> AI Redesign</button>
          <button onClick={() => setShowAIImage(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-[#EC4899] to-[#F97316] text-white shadow-[0_0_15px_rgba(236,72,153,0.3)] hover:shadow-[0_0_25px_rgba(236,72,153,0.5)] transition-all" data-testid="ai-image-btn"><Sparkles size={14} /> AI Image</button>
          <button onClick={() => setShowAddBlock(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] transition-all" data-testid="add-block-btn"><Plus size={14} /> Add Block</button>
          <button onClick={handleSave} disabled={saving || !hasChanges} className="flex items-center gap-1.5 px-5 py-2 rounded-xl text-sm font-medium bg-white text-[#1E1E3A] hover:bg-gray-100 disabled:opacity-40 transition-all" data-testid="save-site-btn"><Save size={14} /> {saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>

      {hasChanges && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-300 px-4 py-2.5 rounded-xl text-xs font-medium" data-testid="unsaved-changes">
          Unsaved changes — Save to update your live site
        </div>
      )}

      <div className="flex gap-5">
        <div className={`${showPreview ? 'w-1/2' : 'w-full'} space-y-3 transition-all`}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sortedBlocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
              {sortedBlocks.map((block) => (
                <SortableBlock key={block.id} block={block} isActive={activeBlock === block.id} onToggle={() => handleToggleBlock(block.id)} onDelete={() => deleteBlock(block.id)} onToggleVis={() => toggleVisibility(block.id)}>
                  {renderBlockEditor(block)}
                </SortableBlock>
              ))}
            </SortableContext>
          </DndContext>
          {blocks.length === 0 && (
            <GlassCard className="p-16 text-center">
              <Layout size={48} className="mx-auto text-[#64748B] mb-4" />
              <p className="text-[#9CA3AF]">No blocks yet. Add your first block!</p>
            </GlassCard>
          )}
        </div>

        {showPreview && (
          <div className="w-1/2 sticky top-0 h-[calc(100vh-140px)]" data-testid="live-preview-panel">
            <GlassCard className="h-full flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <div className="flex items-center gap-2"><Eye size={14} className="text-[#A78BFA]" /><span className="text-xs text-[#9CA3AF] font-medium">Live Preview</span></div>
                <span className="text-[10px] text-[#00CC99]">Updates as you type</span>
              </div>
              <div className="flex-1 overflow-y-auto" ref={previewRef}><LivePreview blocks={blocks} allProducts={allProducts} /></div>
            </GlassCard>
          </div>
        )}
      </div>

      {/* Add Block Dialog */}
      <Dialog open={showAddBlock} onOpenChange={setShowAddBlock}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg">Add Content Block</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            {BLOCK_TYPES.map((bt) => {
              const Icon = blockIcons[bt.type] || Layout;
              return (
                <button key={bt.type} onClick={() => addBlock(bt.type)} className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/5 hover:border-[#8B5CF6]/40 hover:bg-[#8B5CF6]/10 transition-all text-left" data-testid={`add-block-${bt.type}`}>
                  <Icon size={18} className="text-[#A78BFA] mt-0.5 shrink-0" /><div><p className="text-sm font-medium">{bt.label}</p><p className="text-xs text-[#64748B]">{bt.desc}</p></div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Redesign Panel */}
      <Dialog open={showAIPanel} onOpenChange={setShowAIPanel}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-lg flex items-center gap-2"><Wand2 size={18} className="text-[#A78BFA]" /> AI Content Redesign</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-xs text-[#9CA3AF]">Describe how you want to redesign the content.{activeBlock && <span className="text-[#A78BFA]"> (Targeting: {blockLabels[blocks.find(b => b.id === activeBlock)?.type] || activeBlock})</span>}</p>
            <Textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="e.g., Make the hero more luxury-focused..." rows={4} className="bg-white/5 border-white/10 text-white rounded-xl" data-testid="ai-prompt-input" />
            <button onClick={handleAIRedesign} disabled={aiLoading || !aiPrompt.trim()} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white disabled:opacity-50 transition-all" data-testid="ai-generate-btn">
              {aiLoading ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />} {aiLoading ? 'Generating...' : 'Generate Suggestions'}
            </button>
            {aiSuggestions && (
              <div className="border border-[#8B5CF6]/30 bg-[#8B5CF6]/5 p-4 rounded-xl space-y-3" data-testid="ai-suggestions">
                <p className="text-sm text-[#A78BFA] font-medium">AI Suggestions Ready</p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {aiSuggestions.suggested_blocks.map((sb, i) => (
                    <div key={i} className="bg-white/5 p-3 rounded-lg text-xs">
                      <p className="text-white font-medium mb-1">{blockLabels[sb.type] || sb.type}</p>
                      {sb.content.title && <p className="text-[#9CA3AF]">Title: <span className="text-white">{sb.content.title}</span></p>}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={applyAISuggestions} className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white text-sm" data-testid="apply-ai-btn">Apply Changes</button>
                  <button onClick={() => setAiSuggestions(null)} className="flex-1 py-2.5 rounded-xl border border-white/10 text-[#9CA3AF] hover:bg-white/5 text-sm" data-testid="discard-ai-btn">Discard</button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="text-lg flex items-center gap-2"><Palette size={18} className="text-[#A78BFA]" /> Site Settings</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label className="text-[#94A3B8] text-sm">Brand Name</Label><Input value={settingsForm.brand_name || ''} onChange={(e) => setSettingsForm(p => ({...p, brand_name: e.target.value}))} className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" data-testid="settings-brand-name" /></div>
            <div><Label className="text-[#94A3B8] text-sm">Tagline</Label><Input value={settingsForm.tagline || ''} onChange={(e) => setSettingsForm(p => ({...p, tagline: e.target.value}))} className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" data-testid="settings-tagline" /></div>
            <div>
              <Label className="text-[#94A3B8] text-sm">Accent Color</Label>
              <div className="flex items-center gap-3 mt-1">
                <input type="color" value={settingsForm.accent_color || '#8B5CF6'} onChange={(e) => setSettingsForm(p => ({...p, accent_color: e.target.value}))} className="w-10 h-10 border-0 bg-transparent cursor-pointer" />
                <Input value={settingsForm.accent_color || ''} onChange={(e) => setSettingsForm(p => ({...p, accent_color: e.target.value}))} className="bg-white/5 border-white/10 text-white flex-1 rounded-xl" data-testid="settings-accent-color" />
              </div>
            </div>
            <button onClick={handleSaveSettings} className="w-full py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] transition-all text-sm" data-testid="save-settings-btn">Save Settings</button>
          </div>
        </DialogContent>
      </Dialog>

      <AIAgentWidget siteId={siteId} siteName={site?.brand_name || site?.name || 'Site'} onContentChanged={fetchData} />

      {/* AI Image Generation Dialog */}
      <Dialog open={showAIImage} onOpenChange={setShowAIImage}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-lg flex items-center gap-2"><Sparkles size={18} className="text-[#F97316]" /> AI Image Generator</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-xs text-[#9CA3AF]">Describe the image you want to generate for this site's content.</p>
            <Textarea value={aiImagePrompt} onChange={(e) => setAiImagePrompt(e.target.value)} placeholder="e.g., A luxury fashion brand hero image with models wearing elegant evening wear, studio lighting, magazine quality..." rows={3} className="bg-white/5 border-white/10 text-white rounded-xl" data-testid="ai-image-prompt" />
            <button onClick={handleGenerateImage} disabled={aiImageLoading || !aiImagePrompt.trim()} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-[#EC4899] to-[#F97316] text-white disabled:opacity-50 transition-all" data-testid="generate-image-btn">
              {aiImageLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {aiImageLoading ? 'Generating...' : 'Generate Image'}
            </button>
            {aiGeneratedImage && (
              <div className="space-y-3" data-testid="ai-generated-result">
                <div className="rounded-xl overflow-hidden border border-white/10"><img src={aiGeneratedImage} alt="AI Generated" className="w-full h-auto" /></div>
                <p className="text-xs text-[#64748B]">Select a block to apply this image:</p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {sortedBlocks.filter(b => ['hero', 'banner', 'about', 'custom_image'].includes(b.type)).map(block => (
                    <button key={block.id} onClick={() => applyAIImage(block.id, 'image_url')} className="w-full flex items-center gap-2 p-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-[#8B5CF6]/40 hover:bg-[#8B5CF6]/10 transition-all text-left text-sm" data-testid={`apply-to-${block.id}`}>
                      <Image size={14} className="text-[#A78BFA] shrink-0" />
                      <span className="text-white">{blockLabels[block.type] || block.type}</span>
                    </button>
                  ))}
                </div>
                <button onClick={() => { navigator.clipboard.writeText(aiGeneratedImage); toast.success('Image URL copied!'); }} className="w-full py-2 rounded-xl text-xs font-medium bg-white/5 text-[#9CA3AF] border border-white/10 hover:bg-white/10 transition-colors">Copy Image URL</button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
